import React, { useState } from "react";
import { MapPin, Mail, Phone, Clock, Send } from "lucide-react";
import useReveal from "../hooks/useReveal";
import { CLINIC, IMAGES } from "../mock";
import { useToast } from "../hooks/use-toast";
import { sendContact } from "../api";

export default function Contact() {
  useReveal();
  const { toast } = useToast();
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendContact(form);
      toast({ title: "Message sent", description: "Thank you — we’ll be in touch shortly." });
      setForm({ firstName: "", lastName: "", email: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment." });
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-20">
      <section className="relative py-32 bg-charcoal overflow-hidden">
        <div className="absolute inset-0 opacity-40">
          <img src={IMAGES.interior1} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/70 to-charcoal/40" />
        </div>
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 text-center">
          <span className="eyebrow text-gold">Contact Us</span>
          <h1 className="font-display text-5xl lg:text-6xl font-light text-cream mt-5 leading-tight">
            Looking forward to <span className="italic text-gold">your visit</span>
          </h1>
        </div>
      </section>

      <section className="bg-cream py-24">
        <div className="max-w-[1300px] mx-auto px-6 grid lg:grid-cols-2 gap-16">
          {/* Info */}
          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-rule" />
              <span className="eyebrow text-gold">Get In Touch</span>
            </div>
            <h2 className="font-display text-4xl font-light text-ink leading-tight">
              Book your complimentary consultation
            </h2>
            <p className="font-sans text-muted-ink text-[15px] leading-[1.9] mt-6">
              Whether you have a question about a treatment or you’re ready to begin your journey, our team is here to help. Reach out and we’ll respond with care.
            </p>

            <div className="space-y-6 mt-10">
              {[
                { icon: MapPin, label: "Visit", value: CLINIC.address },
                { icon: Mail, label: "Email", value: CLINIC.email, href: `mailto:${CLINIC.email}` },
                { icon: Phone, label: "Call", value: CLINIC.phone, href: `tel:${CLINIC.phone}` },
              ].map((it) => (
                <div key={it.label} className="flex gap-5 items-start">
                  <div className="w-12 h-12 rounded-full border border-gold flex items-center justify-center text-gold shrink-0">
                    <it.icon size={18} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="eyebrow text-muted-ink">{it.label}</div>
                    {it.href ? (
                      <a href={it.href} className="font-display text-xl text-ink hover:text-gold transition-colors">{it.value}</a>
                    ) : (
                      <div className="font-display text-xl text-ink">{it.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-gold/20">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={16} className="text-gold" />
                <span className="eyebrow text-ink">Opening Hours</span>
              </div>
              <ul className="space-y-2">
                {CLINIC.hours.map((h) => (
                  <li key={h.day} className="flex justify-between font-sans text-sm text-muted-ink">
                    <span>{h.day}</span>
                    <span className="text-ink">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Form */}
          <div className="reveal">
            <form onSubmit={submit} className="bg-white rounded-sm p-8 lg:p-10 shadow-sm border border-gold/10">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="First Name" name="firstName" value={form.firstName} onChange={onChange} required />
                <Field label="Last Name" name="lastName" value={form.lastName} onChange={onChange} required />
              </div>
              <div className="mt-5">
                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
              </div>
              <div className="mt-5">
                <label className="eyebrow text-muted-ink block mb-2">Message</label>
                <textarea
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={onChange}
                  required
                  className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold transition-colors font-sans resize-none"
                />
              </div>
              <button type="submit" disabled={sending} className="btn-gold w-full mt-7 flex items-center justify-center gap-2 disabled:opacity-50">
                <Send size={15} /> {sending ? "Sending..." : "Send Message"}
              </button>
            </form>
          </div>
        </div>

        {/* Map */}
        <div className="max-w-[1300px] mx-auto px-6 mt-16">
          <div className="rounded-sm overflow-hidden reveal h-[380px] border border-gold/20">
            <iframe
              title="Aesthetix MD location"
              src="https://www.google.com/maps?q=12020+104+Ave+NW,+Edmonton+AB&output=embed"
              className="w-full h-full grayscale contrast-110"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="eyebrow text-muted-ink block mb-2">{label}</label>
      <input
        {...props}
        className="w-full bg-cream/50 border border-gold/20 px-4 py-3 text-sm text-ink focus:outline-none focus:border-gold transition-colors font-sans"
      />
    </div>
  );
}
